
import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { SidebarCTA } from "../nursing-homes/SidebarCTA";

const BREAKPOINTS = [
  { name: "Mobile S", width: 320, height: 568 },
  { name: "Mobile M", width: 375, height: 667 },
  { name: "Mobile L", width: 425, height: 812 },
  { name: "Tablet", width: 768, height: 1024 },
  { name: "Laptop", width: 1024, height: 768 },
  { name: "Laptop L", width: 1440, height: 900 },
  { name: "Desktop", width: 1920, height: 1080 },
];

const COMPONENTS_TO_TEST = [
  { name: "SidebarCTA", component: <SidebarCTA centerName="Sample Nursing Home" nhSlug="sample" countySlug="dublin" /> },
  { name: "Form Fields", component: (
    <div className="space-y-4 max-w-md">
      <div className="grid w-full gap-1.5">
        <Label htmlFor="name">Name</Label>
        <Input id="name" placeholder="Enter your name" />
      </div>
      <div className="grid w-full gap-1.5">
        <Label htmlFor="email">Email</Label>
        <Input id="email" placeholder="Enter your email" type="email" />
      </div>
      <div className="flex items-center gap-2">
        <Switch id="marketing" />
        <Label htmlFor="marketing">Receive marketing emails</Label>
      </div>
      <Button className="w-full">Submit</Button>
    </div>
  )},
  { name: "Card", component: (
    <Card>
      <CardHeader>
        <CardTitle>Card Title</CardTitle>
      </CardHeader>
      <CardContent>
        <p>This is some content inside a card. It should adapt to different screen sizes appropriately.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="bg-muted p-4 rounded">Column 1</div>
          <div className="bg-muted p-4 rounded">Column 2</div>
        </div>
      </CardContent>
    </Card>
  )},
];

export function ResponsiveTest() {
  const [selectedComponent, setSelectedComponent] = useState(COMPONENTS_TO_TEST[0]);
  const [selectedBreakpoint, setSelectedBreakpoint] = useState(BREAKPOINTS[1]);
  const [orientation, setOrientation] = useState<"portrait" | "landscape">("portrait");

  const toggleOrientation = () => {
    setOrientation(orientation === "portrait" ? "landscape" : "portrait");
  };

  const frameWidth = orientation === "portrait" ? selectedBreakpoint.width : selectedBreakpoint.height;
  const frameHeight = orientation === "portrait" ? selectedBreakpoint.height : selectedBreakpoint.width;

  return (
    <Layout>
      <div className="container py-12">
        <SectionHeader 
          title="Responsive Testing" 
          subtitle="Test components at different screen sizes to ensure they're responsive"
          className="mb-8"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Test Controls</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Select Component</Label>
                  <div className="space-y-1">
                    {COMPONENTS_TO_TEST.map((comp) => (
                      <Button
                        key={comp.name}
                        variant={selectedComponent.name === comp.name ? "default" : "outline"}
                        className="w-full justify-start"
                        onClick={() => setSelectedComponent(comp)}
                      >
                        {comp.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Screen Size</Label>
                  <div className="space-y-1">
                    {BREAKPOINTS.map((bp) => (
                      <Button
                        key={bp.name}
                        variant={selectedBreakpoint.name === bp.name ? "default" : "outline"}
                        className="w-full justify-start text-sm"
                        onClick={() => setSelectedBreakpoint(bp)}
                      >
                        {bp.name} ({bp.width}x{bp.height})
                      </Button>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Orientation</Label>
                  <Button 
                    className="w-full"
                    onClick={toggleOrientation}
                  >
                    Switch to {orientation === "portrait" ? "Landscape" : "Portrait"}
                  </Button>
                </div>

                <div className="bg-muted p-3 rounded-md text-sm">
                  <p className="font-medium">Current Settings:</p>
                  <p>Component: {selectedComponent.name}</p>
                  <p>Device: {selectedBreakpoint.name}</p>
                  <p>Size: {frameWidth}x{frameHeight}</p>
                  <p>Orientation: {orientation}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-9">
            <Card>
              <CardHeader className="border-b">
                <CardTitle>Preview</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-hidden bg-gray-100">
                <div className="w-full h-full flex items-center justify-center p-8">
                  <div 
                    className="border border-gray-300 bg-white overflow-auto shadow-lg transition-all duration-300"
                    style={{ 
                      width: frameWidth + 'px',
                      height: frameHeight + 'px',
                      maxHeight: '80vh',
                      minHeight: '300px',
                      transform: 'scale(0.8)',
                      transformOrigin: 'top left'
                    }}
                  >
                    <div className="p-4">
                      {selectedComponent.component}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Responsive Best Practices</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="border p-4 rounded-md">
                  <h3 className="font-medium text-lg mb-2">Mobile First</h3>
                  <p className="text-sm">Always design and implement for mobile first, then scale up to larger screens using media queries.</p>
                </div>
                
                <div className="border p-4 rounded-md">
                  <h3 className="font-medium text-lg mb-2">Flexible Layouts</h3>
                  <p className="text-sm">Use relative units (%, rem) and max-width instead of fixed widths to ensure layouts adapt.</p>
                </div>
                
                <div className="border p-4 rounded-md">
                  <h3 className="font-medium text-lg mb-2">Touch Targets</h3>
                  <p className="text-sm">Make interactive elements at least 44x44px for better touch usability on mobile devices.</p>
                </div>
                
                <div className="border p-4 rounded-md">
                  <h3 className="font-medium text-lg mb-2">Responsive Typography</h3>
                  <p className="text-sm">Use smaller font sizes on mobile and increase proportionally for larger screens.</p>
                </div>
                
                <div className="border p-4 rounded-md">
                  <h3 className="font-medium text-lg mb-2">Test Across Devices</h3>
                  <p className="text-sm">Always test on actual devices when possible, not just browser emulation.</p>
                </div>
                
                <div className="border p-4 rounded-md">
                  <h3 className="font-medium text-lg mb-2">Accessibility</h3>
                  <p className="text-sm">Ensure interface remains accessible across all screen sizes, including when zoomed.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
